import React, { useCallback, useMemo, useRef } from "react";
import type { FieldValues } from "react-hook-form";
import {
    type BusinessFormRenderProps,
    type FormHandle,
    type FormSubmissionMemoryOptions,
    resolveBusinessFormInitialValues,
} from "@src/components/business-form";
import FormFooter from "@shared/ui/form-footer";
import type { FormSeedResult } from "./formSeed";
import type { FormPanelProps } from "./FormPanel";
import { FormPanel } from "./FormPanel";

/** Пропсы {@link FilterFormPanel}. */
export interface FilterFormPanelProps<TForm extends FieldValues, TFilter>
    extends Omit<FormPanelProps<TForm>, "formProps" | "footer" | "children" | "useDefaultFooter"> {
    /** Фильтр из URL; при наличии имеет приоритет над seed и submission memory. */
    initialFilter: TFilter | null;
    /**
     * Результат подготовки seed поверх defaults из `formConfig`. \
     * При `status: "pending"` форма не монтируется; `initialFilter` отключает ожидание seed.
     */
    seed?: FormSeedResult<TForm>;
    /** Пропсы вложенной формы без источников initial values и submit-обработчика. */
    formProps: Omit<FormPanelProps<TForm>["formProps"], "initialValues" | "seedValues" | "onSubmit">;
    /**
     * Применяет доменный фильтр.
     * @param filter преобразованный фильтр
     */
    onApply: (filter: TFilter) => void | Promise<void>;
    /**
     * Преобразует доменный фильтр в значения формы.
     * @param filter доменный фильтр
     */
    toForm: (filter: TFilter) => TForm;
    /**
     * Преобразует значения формы в доменный фильтр.
     * @param values значения формы
     */
    toDomain: (values: TForm) => TFilter;
    /**
     * Рендерит поля формы.
     * @param form API вложенной формы
     */
    children: (form: BusinessFormRenderProps<TForm>) => React.ReactNode;
    /**
     * Persistence последнего успешного apply. \
     * `BusinessForm` применяет restore только при отсутствии `initialFilter`.
     */
    submissionMemory?: FormSubmissionMemoryOptions<TForm>;
}

/**
 * Filter-форма: поля + footer «Сброс / Применить / Отмена».
 *
 * @template TForm значения формы
 * @template TFilter доменный фильтр
 */
export function FilterFormPanel<TForm extends FieldValues, TFilter>({
    initialFilter,
    seed,
    formProps,
    onApply,
    toForm,
    toDomain,
    children,
    submissionMemory,
    ...formPanelProps
}: FilterFormPanelProps<TForm, TFilter>) {
    const formApiRef = useRef<FormHandle<TForm> | null>(null);
    const seedValues = seed?.values;
    const isSeedPending = seed?.status === "pending";
    const isFormReady = formProps.ready !== false && (initialFilter != null || !isSeedPending);

    const effectiveInitialValues = useMemo(
        () => (initialFilter == null ? undefined : toForm(initialFilter)),
        [initialFilter, toForm],
    );

    const resetTargetValues = useMemo(
        () =>
            resolveBusinessFormInitialValues({
                seedValues,
                defaultValues: formProps.formConfig.defaults(),
            }),
        [formProps.formConfig, seedValues],
    );

    const handleFormSubmit = useCallback(
        async (values: TForm) => {
            await onApply(toDomain(values));
        },
        [onApply, toDomain],
    );

    return (
        <FormPanel<TForm>
            {...formPanelProps}
            formApiRef={formApiRef}
            useDefaultFooter={false}
            footer={({ submitForm }) => (
                <FormFooter.Filter
                    onReset={() => formApiRef.current?.reset(resetTargetValues)}
                    resetDisabled={!isFormReady || isSeedPending}
                    confirmLoading={!isFormReady}
                    onConfirm={submitForm}
                />
            )}
            formProps={{
                ...formProps,
                initialValues: effectiveInitialValues,
                seedValues,
                ready: isFormReady,
                onSubmit: handleFormSubmit,
                submissionMemory,
            }}
        >
            {children}
        </FormPanel>
    );
}
